import {
  DEFAULT_LOCALE_PREFERENCES,
  localeLanguagePatchSchema,
  localePreferencesSchema,
  localeTimezonePatchSchema,
  type ApiError,
  type LocaleLanguage,
  type LocalePreferences,
  type TimezoneOverride,
} from '@molt/shared';
import { createContext, createElement, useContext, type ReactNode } from 'react';
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { get, patch } from './api';
import { queryKeys } from './query-keys';

export const LocalePreferencesContext = createContext<LocalePreferences>(
  DEFAULT_LOCALE_PREFERENCES,
);

export function LocalePreferencesProvider({
  preferences,
  children,
}: {
  preferences: LocalePreferences | null | undefined;
  children: ReactNode;
}) {
  return createElement(
    LocalePreferencesContext.Provider,
    { value: preferences ?? DEFAULT_LOCALE_PREFERENCES },
    children,
  );
}

export function useLocalePreferencesValue(): LocalePreferences {
  return useContext(LocalePreferencesContext);
}

export function getLocalePreferences(): Promise<LocalePreferences> {
  return get('/me/locale-preferences', localePreferencesSchema);
}

export function setLocaleLanguage(language: LocaleLanguage): Promise<LocalePreferences> {
  return patch(
    '/me/locale-preferences/language',
    localePreferencesSchema,
    localeLanguagePatchSchema.parse({ language }),
  );
}

export function setTimezoneOverride(
  timezoneOverride: TimezoneOverride,
): Promise<LocalePreferences> {
  return patch(
    '/me/locale-preferences/timezone',
    localePreferencesSchema,
    localeTimezonePatchSchema.parse({ timezoneOverride }),
  );
}

export function localePreferencesQueryOptions(): UseQueryOptions<
  LocalePreferences,
  ApiError
> {
  return {
    queryKey: queryKeys.meLocale(),
    queryFn: getLocalePreferences,
  };
}

export function useSetLocaleLanguage(): UseMutationResult<
  LocalePreferences,
  ApiError,
  LocaleLanguage
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setLocaleLanguage,
    onSuccess: (preferences) => {
      queryClient.setQueryData(queryKeys.meLocale(), preferences);
      return queryClient.invalidateQueries({ queryKey: queryKeys.meMessageCatalogue() });
    },
  });
}

export function useSetTimezoneOverride(): UseMutationResult<
  LocalePreferences,
  ApiError,
  TimezoneOverride
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setTimezoneOverride,
    onSuccess: (preferences) => {
      queryClient.setQueryData(queryKeys.meLocale(), preferences);
    },
  });
}
